/**
 * Reward rules, version 1 — the single source of reward values. The database
 * stores the same JSON in `reward_rule_versions` (see migrations) and the
 * finalize transaction reads it from there; a DB test asserts they match.
 */
export const REWARD_RULES = {
  version: 3,
  session: {
    /** Sessions shorter than this earn no XP, streak credit or leaderboard points. */
    minQuestions: 5,
    baseXp: 5,
    xpPerFirstAttemptCorrect: 1,
    /** Bonus for 100% first-attempt accuracy (no hints) with at least `perfectMinQuestions`. */
    perfectBonusXp: 5,
    perfectMinQuestions: 10,
    /**
     * Asking for a hint anywhere in a session forfeits all XP for that session
     * (so leaderboard points too). One-time bonuses are not lost, only deferred:
     * they are paid on the first hint-free qualifying session instead. The
     * automatic answer reveal after repeated misses is not a hint.
     */
    hintForfeitsXp: true,
    /** Bonus the first time a path lesson is passed in a hint-free session. */
    lessonFirstCompletionBonusXp: 10,
    /** Bonus once an assignment's required sessions are completed, paid on a hint-free session. */
    assignmentCompletionBonusXp: 10,
    /**
     * Repeating content (a lesson, assignment or practice configuration) never
     * costs XP while the learner is still learning it — reinforcement is the point.
     * Once every skill it practises is already mastered, the multiplier depends on
     * how many post-mastery completions of that content came earlier the same local
     * day: three more at full XP, then 50%, 25%, then nothing. Stops farming
     * content that is no longer a challenge. (v1 reduced every repeat: 1, 0.5, 0.25, 0.)
     */
    masteredRepeatMultipliers: [1, 1, 1, 0.5, 0.25, 0],
  },
  leaderboard: {
    /** Only sessions for that classroom's assignments score on its leaderboard. */
    classroomActivityOnly: true,
    /** Leaderboard points equal the session XP (after repeat reduction), capped per student per local day. */
    dailyCapPerStudent: 100,
    /** Ties: points desc, then who reached the total first, then display name. */
    tieBreak: ['points_desc', 'reached_at_asc', 'display_name_asc'],
  },
  streak: {
    /** A day counts toward the streak with at least one qualifying completed session. */
    minQuestionsPerSession: 5,
  },
} as const;

export type RewardRules = typeof REWARD_RULES;

export interface BadgeDefinition {
  id: string;
  name: string;
  description: string;
  /** Icon key rendered by the UI. */
  icon: 'spark' | 'target' | 'flame' | 'star' | 'trophy' | 'flag' | 'check';
}

export const BADGES: readonly BadgeDefinition[] = [
  { id: 'first-session', name: 'First notes', description: 'Finished your first practice session.', icon: 'spark' },
  { id: 'perfect-session', name: 'Clean read', description: 'Got every note right on the first try in a session of 10 or more.', icon: 'target' },
  { id: 'streak-3', name: 'Three in a row', description: 'Practiced three days in a row.', icon: 'flame' },
  { id: 'streak-7', name: 'Full week', description: 'Practiced seven days in a row.', icon: 'flame' },
  { id: 'streak-30', name: 'Month of music', description: 'Practiced thirty days in a row.', icon: 'flame' },
  { id: 'xp-100', name: '100 XP', description: 'Earned 100 XP.', icon: 'star' },
  { id: 'xp-500', name: '500 XP', description: 'Earned 500 XP.', icon: 'star' },
  { id: 'xp-2000', name: '2,000 XP', description: 'Earned 2,000 XP.', icon: 'star' },
  { id: 'assignment-complete', name: 'Assignment done', description: 'Completed your first class assignment.', icon: 'check' },
  { id: 'milestone-staff-reader', name: 'Staff reader', description: 'Named every natural note on the staff.', icon: 'flag' },
  { id: 'milestone-ledger-reader', name: 'Ledger-line reader', description: 'Read notes up to three ledger lines away.', icon: 'flag' },
  { id: 'milestone-accidental-reader', name: 'Accidental reader', description: 'Read sharps and flats across the staff.', icon: 'flag' },
  { id: 'milestone-note-reading-graduate', name: 'Note-reading graduate', description: 'Passed the cumulative challenge.', icon: 'trophy' },
  // Rhythm path unit tests.
  { id: 'milestone-steady-pulse', name: 'Steady pulse', description: 'Passed the rhythm unit test on 2/4, 4/4 and 2/2.', icon: 'flag' },
  { id: 'milestone-beat-grouper', name: 'Beat grouper', description: 'Passed the rhythm unit test on 3/4, 5/4, 6/4 and 7/4.', icon: 'flag' },
  { id: 'milestone-subdivider', name: 'Subdivider', description: 'Passed the rhythm unit test on eighths, sixteenths, dots and ties.', icon: 'flag' },
  { id: 'milestone-triplet-tapper', name: 'Triplet tapper', description: 'Passed the rhythm unit test on triplets.', icon: 'flag' },
  { id: 'milestone-compound-counter', name: 'Compound counter', description: 'Passed the rhythm unit test on 6/8, 9/8 and 12/8.', icon: 'flag' },
  { id: 'milestone-rhythm-graduate', name: 'Rhythm graduate', description: 'Passed the final rhythm test.', icon: 'trophy' },
];

export const BADGE_BY_ID: ReadonlyMap<string, BadgeDefinition> = new Map(BADGES.map((b) => [b.id, b]));
